import { useState, useCallback } from 'react';
import { Alert } from 'react-native';
import { useStripe } from '@stripe/stripe-react-native';
import { usePaymentsContext } from '../providers/PaymentsProvider';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId, validateEmail } from '../../shared/utils/validation';
import { PaymentIntent } from '@stripe/stripe-react-native';

type CheckoutResult =
  | { type: 'subscription'; customerId: string }
  | { type: 'payment'; paymentIntent: PaymentIntent.Result };

interface UseCheckoutOptions {
  onSuccess?: (result: CheckoutResult) => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
  showErrorAlert?: boolean;
  showSuccessAlert?: boolean;
}

interface UseCheckoutReturn {
  loading: boolean;
  error: string | null;
  // Backward compatibility getters
  isLoading: boolean; // Maps to loading
  isError: boolean; // Maps to !!error
  createSubscription: (params: {
    priceId: string;
    customerId?: string;
    customerEmail?: string;
    metadata?: Record<string, string>;
    trialPeriodDays?: number;
  }) => Promise<boolean>;
  createPaymentIntent: (params: {
    amount: number;
    currency: string;
    customerId?: string;
    description?: string;
    metadata?: Record<string, string>;
  }) => Promise<string | null>;
  processPayment: (
    clientSecret: string,
    billingDetails?: {
      email?: string;
      name?: string;
      phone?: string;
    }
  ) => Promise<boolean>;
}

export const useCheckout = (
  options: UseCheckoutOptions = {}
): UseCheckoutReturn => {
  const {
    onSuccess,
    onError,
    onLoading,
    showErrorAlert = true,
    showSuccessAlert = true,
  } = options;

  const { initPaymentSheet, presentPaymentSheet, confirmPayment } = useStripe();
  const { initialized } = usePaymentsContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      onError?.(errorMessage);

      if (showErrorAlert) {
        Alert.alert('Payment Error', errorMessage);
      }

      console.error('Checkout error:', errorMessage);
    },
    [onError, showErrorAlert]
  );

  const handleSuccess = useCallback(
    (result: CheckoutResult) => {
      onSuccess?.(result);

      if (showSuccessAlert) {
        Alert.alert('Success', 'Payment completed successfully!');
      }
    },
    [onSuccess, showSuccessAlert]
  );

  const createSubscription = useCallback(
    async (params: {
      priceId: string;
      customerId?: string;
      customerEmail?: string;
      metadata?: Record<string, string>;
      trialPeriodDays?: number;
    }): Promise<boolean> => {
      if (!initialized) {
        handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
        return false;
      }

      if (!validateStripeId(params.priceId, 'price')) {
        handleError(ERROR_MESSAGES.INVALID_PRICE_ID);
        return false;
      }

      if (
        params.customerId &&
        !validateStripeId(params.customerId, 'customer')
      ) {
        handleError(ERROR_MESSAGES.INVALID_CUSTOMER_ID);
        return false;
      }

      if (params.customerEmail && !validateEmail(params.customerEmail)) {
        handleError('Invalid customer email');
        return false;
      }

      setLoading(true);
      setError(null);
      onLoading?.(true);

      try {
        // Create subscription setup on server
        const response = await fetch(
          '/api/payments/create-subscription-setup',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(
            errorData.error || 'Failed to create subscription setup'
          );
        }

        const {
          setupIntentClientSecret,
          customerId: returnedCustomerId,
          customerEphemeralKeySecret,
        } = await response.json();

        if (!setupIntentClientSecret) {
          throw new Error('No setup intent client secret returned from server');
        }

        // Initialize the payment sheet
        const { error: initError } = await initPaymentSheet({
          setupIntentClientSecret,
          customerId: returnedCustomerId,
          customerEphemeralKeySecret,
          merchantDisplayName: 'Your App Name', // TODO: Make this configurable
          allowsDelayedPaymentMethods: true,
        });

        if (initError) {
          throw new Error(
            initError.message || 'Failed to initialize payment sheet'
          );
        }

        // Present the payment sheet
        const { error: presentError } = await presentPaymentSheet();

        if (presentError) {
          // User cancelled or error occurred
          if (presentError.code === 'Canceled') {
            return false; // User cancelled, don't show error
          }
          throw new Error(
            presentError.message || ERROR_MESSAGES.PAYMENT_FAILED
          );
        }

        // Payment successful
        handleSuccess({ type: 'subscription', customerId: returnedCustomerId });
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create subscription';
        handleError(errorMessage);
        return false;
      } finally {
        setLoading(false);
        onLoading?.(false);
      }
    },
    [
      initialized,
      initPaymentSheet,
      presentPaymentSheet,
      handleError,
      handleSuccess,
      onLoading,
    ]
  );

  const createPaymentIntent = useCallback(
    async (params: {
      amount: number;
      currency: string;
      customerId?: string;
      description?: string;
      metadata?: Record<string, string>;
    }): Promise<string | null> => {
      if (!initialized) {
        handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
        return null;
      }

      if (params.amount <= 0) {
        handleError('Amount must be greater than 0');
        return null;
      }

      if (
        params.customerId &&
        !validateStripeId(params.customerId, 'customer')
      ) {
        handleError(ERROR_MESSAGES.INVALID_CUSTOMER_ID);
        return null;
      }

      setLoading(true);
      setError(null);
      onLoading?.(true);

      try {
        const response = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || 'Failed to create payment intent');
        }

        const { client_secret } = await response.json();

        if (!client_secret) {
          throw new Error('No client secret returned from server');
        }

        return client_secret;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to create payment intent';
        handleError(errorMessage);
        return null;
      } finally {
        setLoading(false);
        onLoading?.(false);
      }
    },
    [initialized, handleError, onLoading]
  );

  const processPayment = useCallback(
    async (
      clientSecret: string,
      billingDetails?: {
        email?: string;
        name?: string;
        phone?: string;
      }
    ): Promise<boolean> => {
      if (!initialized) {
        handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
        return false;
      }

      if (billingDetails?.email && !validateEmail(billingDetails.email)) {
        handleError('Invalid email address');
        return false;
      }

      setLoading(true);
      setError(null);
      onLoading?.(true);

      try {
        const { error, paymentIntent } = await confirmPayment(clientSecret, {
          paymentMethodType: 'Card',
          paymentMethodData: {
            billingDetails,
          },
        });

        if (error) {
          throw new Error(error.message || ERROR_MESSAGES.PAYMENT_FAILED);
        }

        if (paymentIntent?.status === 'Succeeded') {
          handleSuccess({ type: 'payment', paymentIntent });
          return true;
        } else {
          throw new Error('Payment was not completed');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : ERROR_MESSAGES.PAYMENT_FAILED;
        handleError(errorMessage);
        return false;
      } finally {
        setLoading(false);
        onLoading?.(false);
      }
    },
    [initialized, confirmPayment, handleError, handleSuccess, onLoading]
  );

  return {
    loading,
    error,
    // Backward compatibility getters
    isLoading: loading,
    isError: !!error,
    createSubscription,
    createPaymentIntent,
    processPayment,
  };
};
